import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Spinner from "../../Atoms/Spinner";
import Section from "../../Atoms/Section";
import H1 from "../../Atoms/H1";
import * as api from "../../../api";
import EnhancedTable from "../../Components/EnhancedTable";
import CSVExportButton from "../../Components/CSVExportButton";
import BlueButton from "../../Atoms/BlueButton";
import * as XLSX from 'xlsx';
import INVITATION_ID_BY_EMAIL from "./invitationIdByEmail";

// Extract unique fiche options from the static mapping
const FICHE_OPTIONS = [...new Set(Object.values(INVITATION_ID_BY_EMAIL))].sort();

const FD100CeremonyRSVP = (props) => {
    const [rsvps, setRsvps] = useState(null);
    const [uniqueRsvps, setUniqueRsvps] = useState(null);
    const [tableData, setTableData] = useState(null);
    const [approvalMap, setApprovalMap] = useState({});
    const [ficheMap, setFicheMap] = useState({});
    const [qrSentMap, setQrSentMap] = useState({});
    const navigate = useNavigate();

    useEffect(() => {
        if (props.fullUserLoading) return;
        if (!props.fullUser?.user) navigate("/");
        if (props.fullUser?.user && props.fullUser.user.role !== "admin") navigate("/");
    }, [props.fullUserLoading, props.fullUser, navigate]);

    useEffect(() => {
        api.getAllRSVPs().then(res => {
            setRsvps(res.data);
        }).catch(console.error);
    }, []);

    // Deduplicate and store unique list
    useEffect(() => {
        if (!rsvps) return;

        const seen = new Set();
        const unique = rsvps
            .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
            .filter(r => {
                const key = `${(r.first_name || '').toLowerCase().trim()}|${(r.last_name || '').toLowerCase().trim()}|${(r.email || '').toLowerCase().trim()}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });

        setUniqueRsvps(unique);

        // Init approval map from DB values
        const aMap = {};
        unique.forEach(r => {
            aMap[r.id] = !!r.approved;
        });
        setApprovalMap(aMap);

        // Init fiche map: use DB value if set, otherwise fall back to static mapping
        const fMap = {};
        unique.forEach(r => {
            const emailKey = (r.email || '').trim().toLowerCase();
            fMap[r.id] = r.fiche || INVITATION_ID_BY_EMAIL[emailKey] || '';
        });
        setFicheMap(fMap);

        // Init qr sent map from DB values
        const qMap = {};
        unique.forEach(r => {
            qMap[r.id] = !!r.qr_sent;
        });
        setQrSentMap(qMap);
    }, [rsvps]);

    const toggleApproval = useCallback((rsvpId) => {
        const newValue = !approvalMap[rsvpId];
        setApprovalMap(prev => ({ ...prev, [rsvpId]: newValue }));
        api.updateRSVPApproval({ id: rsvpId, approved: newValue }).catch(err => {
            console.error(err);
            // Revert on error
            setApprovalMap(prev => ({ ...prev, [rsvpId]: !newValue }));
        });
    }, [approvalMap]);

    const toggleQrSent = useCallback((rsvpId) => {
        const newValue = !qrSentMap[rsvpId];
        setQrSentMap(prev => ({ ...prev, [rsvpId]: newValue }));
        api.updateRSVPQrSent({ id: rsvpId, qr_sent: newValue }).catch(err => {
            console.error(err);
            setQrSentMap(prev => ({ ...prev, [rsvpId]: !newValue }));
        });
    }, [qrSentMap]);

    const handleFicheChange = useCallback((rsvpId, newValue) => {
        const oldValue = ficheMap[rsvpId];
        setFicheMap(prev => ({ ...prev, [rsvpId]: newValue }));
        api.updateRSVPFiche({ id: rsvpId, fiche: newValue }).catch(err => {
            console.error(err);
            // Revert on error
            setFicheMap(prev => ({ ...prev, [rsvpId]: oldValue }));
        });
    }, [ficheMap]);

    // Build table data whenever unique list, approval map, or fiche map changes
    useEffect(() => {
        if (!uniqueRsvps) return;

        const _tableData = [];
        _tableData.push(["Prénom", "Nom", "Société", "Email", "Fiche", "Date d'inscription", "Approuvé", "QR envoyé"]);

        for (let i = 0; i < uniqueRsvps.length; i++) {
            const r = uniqueRsvps[i];
            const date = new Date(r.created_at);
            const formattedDate = date.toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });

            _tableData.push([
                r.first_name,
                r.last_name,
                r.company || '',
                r.email,
                {
                    select: true,
                    value: ficheMap[r.id] || '',
                    options: FICHE_OPTIONS,
                    onChange: r.id
                },
                formattedDate,
                {
                    button: true,
                    label: approvalMap[r.id] ? '✓ Approuvé' : '—',
                    onClick: { action: 'approval', id: r.id }
                },
                {
                    button: true,
                    label: qrSentMap[r.id] ? '✓ Envoyé' : '—',
                    onClick: { action: 'qrSent', id: r.id }
                }
            ]);
        }
        setTableData(_tableData);
    }, [uniqueRsvps, approvalMap, ficheMap, qrSentMap]);

    const handleButtonClick = useCallback((data) => {
        if (data.action === 'approval') toggleApproval(data.id);
        else if (data.action === 'qrSent') toggleQrSent(data.id);
    }, [toggleApproval, toggleQrSent]);

    // Export data (without React elements) for CSV/XLSX
    const getExportData = useCallback(() => {
        if (!uniqueRsvps) return [];
        const rows = [["Prénom", "Nom", "Société", "Email", "Fiche", "Date d'inscription", "Approuvé", "QR envoyé"]];
        for (const r of uniqueRsvps) {
            const date = new Date(r.created_at);
            const formattedDate = date.toLocaleDateString('fr-FR', {
                day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
            });
            rows.push([
                r.first_name,
                r.last_name,
                r.company || '',
                r.email,
                ficheMap[r.id] || '',
                formattedDate,
                approvalMap[r.id] ? 'Oui' : 'Non',
                qrSentMap[r.id] ? 'Oui' : 'Non'
            ]);
        }
        return rows;
    }, [uniqueRsvps, approvalMap, ficheMap, qrSentMap]);

    if (!tableData) return <Section className="padding"><Spinner /></Section>;

    const exportData = getExportData();

    return (
        <>
            <Section className="padding flexblock">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <H1>RSVP Cérémonie FD100 ({tableData.length - 1})</H1>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <CSVExportButton data={exportData} filename="fd100_ceremony_rsvp.csv" />
                        <BlueButton onClick={() => {
                            const data = getExportData();
                            const ws = XLSX.utils.aoa_to_sheet(data);
                            ws['!cols'] = data[0].map(() => ({ wch: 25 }));
                            const wb = XLSX.utils.book_new();
                            XLSX.utils.book_append_sheet(wb, ws, 'RSVP');
                            XLSX.writeFile(wb, 'fd100_ceremony_rsvp.xlsx');
                        }}>Télécharger au format XLSX</BlueButton>
                    </div>
                </div>
            </Section>
            <Section>
                <EnhancedTable data={tableData} onButtonClick={handleButtonClick} onSelectChange={handleFicheChange} />
            </Section>
        </>
    );
};

export default FD100CeremonyRSVP;
